import axios from 'axios';
import { useEffect, useState, useCallback } from 'react';

export const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TOKEN_KEY = 'clax:token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

export const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const errMsg = (e, fallback = 'Something went wrong') => e?.response?.data?.detail || e?.message || fallback;

/** Generic fetch hook: const { data, loading, error, reload, setData } = useApi('/studio/overview') */
export function useApi(path, { enabled = true, deps = [] } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    if (!enabled || !path) return;
    setLoading(true);
    try {
      const r = await api.get(path);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled, ...deps]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load, setData };
}

/** Debounced server search */
export function useSearch(q, delay = 250) {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const s = q.trim();
    if (!s) { setRes(null); return; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/search', { params: { q: s } });
        if (alive) setRes(r.data);
      } catch { if (alive) setRes({ movies: [], shows: [], podcasts: [], episodes: [], people: [], genres: [] }); }
      finally { if (alive) setLoading(false); }
    }, delay);
    return () => { alive = false; clearTimeout(t); };
  }, [q, delay]);
  return { res, loading };
}
